import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Clock3, ExternalLink, MapPin, Phone, ShoppingBag, Truck } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { useStore } from "../context/StoreContext";
import { RestaurantChangeDialog } from "../components/RestaurantChangeDialog";
import { usePageMetadata } from "../hooks/usePageMetadata";
import { restaurantOpenStatus } from "../lib/restaurantHours";
import PublicPageLayout from "./PublicPageLayout";

const RestaurantHours = ({ restaurant }) => {
  const status = restaurantOpenStatus(restaurant.hours);
  return <p className="mt-2 flex gap-2 text-sm text-brand-mute">
    <Clock3 className="h-4 w-4 shrink-0 text-brand-red" />
    <span>{status && <strong className={status.open ? "text-green-700" : "text-brand-red"}>{status.label} · </strong>}{restaurant.hours_summary || "See restaurant for opening hours"}</span>
  </p>;
};

export default function RestaurantSelection() {
  const navigate = useNavigate();
  const { cart, cartRestaurant, clear } = useStore();
  const [restaurants, setRestaurants] = useState(null);
  const [error, setError] = useState("");
  const [target, setTarget] = useState(null);
  usePageMetadata({ title: "Order Online | Lucky's Kitchen", canonicalPath: "/order" });

  useEffect(() => {
    api.get("/restaurants")
      .then(({ data }) => setRestaurants(data))
      .catch((e) => { setError(errMsg(e)); setRestaurants([]); });
  }, []);

  const choose = (restaurant) => {
    if (cart.length && cartRestaurant.id !== restaurant.id) setTarget(restaurant);
    else navigate(`/order/${restaurant.slug}`);
  };

  return (
    <PublicPageLayout>
      <section className="mx-auto max-w-7xl px-4 py-12 sm:px-8 sm:py-16 lg:px-16" data-testid="restaurant-selection-page">
        <div className="max-w-2xl">
          <p className="text-xs font-bold uppercase tracking-[0.22em] text-brand-red">Order online</p>
          <h1 className="mt-3 font-serif text-4xl font-bold text-brand-ink sm:text-5xl">Choose your restaurant</h1>
          <p className="mt-4 leading-relaxed text-brand-mute">Menus, prices and delivery options vary by location. Choose where you would like to order from.</p>
        </div>

        {restaurants === null ? (
          <p className="py-16 text-brand-mute" role="status">Loading restaurants…</p>
        ) : error ? (
          <div className="mt-10 rounded-3xl bg-white p-8 ring-1 ring-brand-red/15" role="alert">
            <p className="font-semibold text-brand-red">{error}</p>
            <button onClick={() => window.location.reload()} className="mt-4 text-sm font-bold text-brand-brown underline">Try again</button>
          </div>
        ) : restaurants.length === 0 ? (
          <div className="mt-10 rounded-3xl bg-white p-8 text-brand-mute">Online ordering is not available right now. Please check again later.</div>
        ) : (
          <div className="mt-10 grid gap-6 lg:grid-cols-2">
            {restaurants.map((restaurant) => (
              <article key={restaurant.id} className="flex flex-col rounded-3xl bg-white p-6 shadow-lg shadow-brand-brown/5 ring-1 ring-brand-brown/10 sm:p-8" data-testid={`restaurant-card-${restaurant.slug}`}>
                <h2 className="font-serif text-2xl font-bold text-brand-ink">{restaurant.display_name}</h2>
                <p className="mt-3 flex gap-2 text-sm leading-relaxed text-brand-mute"><MapPin className="mt-0.5 h-4 w-4 shrink-0 text-brand-red" />{restaurant.address}</p>
                <RestaurantHours restaurant={restaurant} />
                {restaurant.phone && <a href={`tel:${restaurant.phone}`} className="mt-2 flex gap-2 text-sm font-semibold text-brand-brown hover:text-brand-red"><Phone className="h-4 w-4" />{restaurant.phone}</a>}
                <div className="mt-5 flex flex-wrap gap-2 text-xs font-bold uppercase tracking-wider">
                  {restaurant.delivery_enabled && <span className="inline-flex items-center gap-1.5 rounded-full bg-brand-beige px-3 py-1.5"><Truck className="h-3.5 w-3.5" />Delivery</span>}
                  {restaurant.pickup_enabled && <span className="inline-flex items-center gap-1.5 rounded-full bg-brand-beige px-3 py-1.5"><ShoppingBag className="h-3.5 w-3.5" />Pickup</span>}
                </div>
                {restaurant.delivery_enabled && <p className="mt-4 text-sm text-brand-mute">Delivery ₹{restaurant.delivery_charge} · Minimum order ₹{restaurant.min_order}</p>}
                <div className="mt-auto flex flex-wrap gap-3 pt-7">
                  <button onClick={() => choose(restaurant)} className="rounded-full bg-brand-red px-6 py-3 text-sm font-bold uppercase tracking-wider text-brand-cream" data-testid={`restaurant-order-${restaurant.slug}`}>View menu &amp; order</button>
                  {restaurant.maps_url && <a href={restaurant.maps_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 rounded-full bg-brand-beige px-5 py-3 text-sm font-bold text-brand-brown">Directions <ExternalLink className="h-4 w-4" /></a>}
                </div>
              </article>
            ))}
          </div>
        )}
        <Link to="/" className="mt-10 inline-block text-sm font-bold text-brand-brown underline">Back to Lucky's Kitchen</Link>
      </section>
      <RestaurantChangeDialog
        open={Boolean(target)}
        onOpenChange={(open) => !open && setTarget(null)}
        restaurantName={cartRestaurant.display_name}
        onConfirm={() => { const slug = target?.slug; clear(); setTarget(null); if (slug) navigate(`/order/${slug}`); }}
      />
    </PublicPageLayout>
  );
}

